import React, { useState, useEffect, useRef } from 'react';
import Icon, { IconType } from '../Icon/Icon';
import { toast } from 'react-toastify';
import { UiValuePairs } from '../../utils/CommonTypes/UserTypes';
import { EasyImage } from '../Tools/EasyImage';
import { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { IFileInfo } from '../../redux/types';

interface IFilePickerProps {
  fileTypes: 'image' | 'pdf' | 'video' | 'all';
  valueSelected: Function;
}

export interface IfilePickerResponse {
  files: IClientFile[];
}

export interface IClientFile {
  Value: string;
  Name: string;
  Id: string;
  IsImage: boolean;
}

type FileType = 'pdf' | 'image' | 'video' | 'text' | 'other';

export const FilePicker = (props: IFilePickerProps) => {
  const [info, setFileInfo] = useState<IFileInfo>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showOptions, setShowOptions] = useState<boolean>();
  const [files, setFiles] = useState<IClientFile[]>([]);

  useEffect(() => {
    Init();
  }, [props.fileTypes]);

  const Init = () => {
    store.server.getApi<ServerResponse<IfilePickerResponse>>(`../file/clientFiles`).then((x) => {
      if (x.Success) {
        setFiles(x.Value.files);
      } else {
        toast.warning(x.Message);
      }
    });
  };
  const selectExistingFile = (x: IClientFile) => {
    let fi = getFileInfo(x.Value);
    if (props.valueSelected) props.valueSelected(x.Value);
    setFileInfo(fi ?? undefined);
    setShowOptions(false);
  };

  const removeFile = (file: IClientFile) => {
    if (window.confirm(`Are you sure you want to remove this "${file.Name}" file?`)) {
      store.server.postApi<ServerResponse<IfilePickerResponse>>(`../File/RemoveClientFile/${file.Id}`, {}).then((x) => {
        if (x.Success) {
          Init();
        } else {
          toast.warning(x.Message);
        }
      });
    }
  };

  const onChangeFile = async (e) => {
    if (e.target.files) {
      let file = await isFileValid(e.target.files);

      if (!file) {
        e.target.files = null;
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      if (e.target && e.target.value) e.target.value = null;

      if (fileInputRef.current) fileInputRef.current.value = '';

      store.server.postApi<ServerResponse<IfilePickerResponse>>(`../File/SaveClientFile`, {}, [file.file]).then((x) => {
        if (x.Success) {
          Init();
        } else {
          toast.warning(x.Message);
        }
      });

      setFileInfo(file.fileInfo);
      setShowOptions(false);
    }
  };

  const isFileValid = async (files: FileList) => {
    if (!files) return;
    let file = files[0];

    let size = file.size / 1024 / 1024;
    let name = file.name;
    let fileInfo = getFileInfo(file.name);
    if (!fileInfo) return null;

    if (fileInfo?.type === 'video' && size > 1024) {
      //to big
      toast.warning(`The file "${name}" is too large. The max file size is 1 GB`);
      return null;
    } else if (fileInfo?.type === 'pdf' && size > 50) {
      //to big
      toast.warning(`The file "${name}" is too large. The max file size is 50 MB`);
      return null;
    } else if (size > 20) {
      //to big
      toast.warning(`The file "${name}" is too large. The max file size is 20 MB`);
      return null;
    }

    fileInfo.urlSource = (await readURL(file)) as string;

    return { file, fileInfo };
  };

  const getFileInfo = (filePath: string | undefined) => {
    if (!filePath || typeof filePath !== typeof ' ') return null;

    let ext = filePath.substring(filePath.lastIndexOf('.')).toLowerCase();
    let isPdf = ext === '.pdf';
    let isImage = ext === '.jpg' || ext === '.jpeg' || ext === '.png' || ext === '.tif' || ext === '.tiff' || ext === '.gif' || ext === '.webp' || ext === '.heic';
    let isVideo = ['.mov', '.mp4'].find((x) => x === ext);
    let isText = ['.csv', '.xml', '.txt'].find((x) => x === ext);
    let type: FileType = isPdf ? 'pdf' : isImage ? 'image' : isVideo ? 'video' : isText ? 'text' : 'other';

    let result: IFileInfo = { type: type, path: filePath, ext: ext };
    return result;
  };

  const readURL = (file) => {
    return new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = (e) => res(e.target.result);
      reader.onerror = (e) => rej(e);
      reader.readAsDataURL(file);
    });
  };

  const nameOrFile = (x: UiValuePairs) => {
    let file = getFileInfo(x.Value);
    if (file && file.path) {
      switch (file.type) {
        case 'image':
          return (
            <div className="flex-between">
              <div className="thumbnail-1 click">
                <EasyImage src={file.path} />
              </div>
              {/* <div className="thumbnail-1 click"><EasyImage imageSrc={[{alt:x.Name, path:file.path}]} name={x.Name} onClick={()=>{selectExistingFile(x)}}  /></div> */}
            </div>
          );
        case 'pdf':
          return (
            <div>
              <a href={file.path}>
                <Icon type={IconType.filePdf} /> {x.Name}
              </a>
            </div>
          );

        case 'video':
          return (
            <div>
              <a href={file.path} target={'_blank'}>
                <Icon type={IconType.video} /> {x.Name}
              </a>
            </div>
          );
        case 'text':
        case 'other':
          return (
            <div>
              <a href={file.path} target={'_blank'}>
                <Icon type={IconType.fileInvoice} /> {x.Name}
              </a>
            </div>
          );
          break;
      }
    }
    return <strong>{x.Name}</strong>;
  };

  return (
    <>
      <div className="file-picker existing-file-container">
        <div className="images">
          {files
            .filter((x) => x.IsImage)
            .map((x, i) => {
              return (
                <div key={`efo-${i}`} className="existing-file-item">
                  {nameOrFile(x)}
                  <span
                    className="remove text-danger"
                    onClick={() => {
                      removeFile(x);
                    }}>
                    <Icon type={IconType.trashO} />
                  </span>
                </div>
              );
            })}
          {files
            .filter((x) => !x.IsImage)
            .map((x, i) => {
              return (
                <div key={`efo-${i}`} className="existing-file-item">
                  {nameOrFile(x)}
                  <span
                    className="remove text-danger"
                    onClick={() => {
                      removeFile(x);
                    }}>
                    <Icon type={IconType.trashO} />
                  </span>
                </div>
              );
            })}
        </div>

        <div className="addmore">
          <h4>Upload File</h4>
          <input type="file" className="form-control" ref={fileInputRef} onChange={onChangeFile} accept={'.pdf,.jpg,.txt,.xml,.docx,.doc,.xlsx,.xls,.png,.webp,.heic,.gif,.mp4,.jpeg,.csv,.ppt,.pptx'} />
        </div>
      </div>
    </>
  );
};
